'use client';

import * as React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import * as LucideIcons from 'lucide-react';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { cn } from '@/lib/utils';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { Typography } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';

type IntegrationTab = {
    id: string;
    title: string;
    description: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconColor: string;
};

type Service = {
    id: string;
    title: string;
    description: string;
    icon?: string | null;
    features?: string[] | null;
};

export function HomemlmServices({ initialServices }: { initialServices?: Service[] }) {
    const locale = useLocale();
    const t = useTranslations('home.ecommerceIntegration');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });
    const [services, setServices] = React.useState<Service[]>(() => initialServices ?? []);
    const [isLoading, setIsLoading] = React.useState(!initialServices?.length);

    const content = {
        pillLabel: t('pillLabel'),
        title: t('title'),
        subtitle: t('subtitle'),
        moreFeaturesLink: 'mlm-software-services',
        exploreMoreButton: t('exploreMoreButton') || 'Explore More',
    };

    React.useEffect(() => {
        if (initialServices?.length) {
            setServices(initialServices);
            setIsLoading(false);
            return;
        }
        const fetchServices = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`/api/services?locale=${locale}&showOnHomePage=true`, {
                    cache: 'no-store',
                });
                if (response.ok) {
                    const data = await response.json();
                    setServices(Array.isArray(data) ? data : []);
                } else {
                    setServices([]);
                }
            } catch (error) {
                setServices([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchServices();
    }, [locale, initialServices?.length]);

    const getIconComponent = (iconName?: string | null) => {
        if (!iconName) return null;
        const IconComponent = (LucideIcons as any)[iconName] as React.ComponentType<{ className?: string }> | undefined;
        return IconComponent || null;
    };

    const iconColors = [
        'text-green-500',
        'text-orange-500',
        'text-blue-500',
        'text-yellow-500',
        'text-purple-500',
        'text-indigo-500',
    ];

    const tabs: IntegrationTab[] = React.useMemo(() => {
        return services
            .filter((service) => service.title) // Filter out services without title
            .map((service, index) => {
                // Create unique ID from title (lowercase, replace spaces with hyphens)
                const id = service.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '') || `tab-${index}`;

                const IconComponent = getIconComponent(service.icon);
                const DefaultIcon = LucideIcons.Store;

                return {
                    id,
                    title: service.title,
                    description: service.description,
                    icon: IconComponent || DefaultIcon,
                    iconColor: iconColors[index % iconColors.length] ?? 'text-gray-600 dark:text-gray-400',
                };
            });
    }, [services]);

    const [activeTab, setActiveTab] = React.useState('');
    const [isTransitioning, setIsTransitioning] = React.useState(false);

    // Set active tab when tabs are loaded
    React.useEffect(() => {
        if (tabs.length > 0 && (!activeTab || !tabs.find(t => t.id === activeTab))) {
            setActiveTab(tabs[0].id);
        }
    }, [tabs]);

    const activeTabData = React.useMemo(
        () => tabs.find((tab) => tab.id === activeTab) || tabs[0],
        [tabs, activeTab]
    );

    const handleTabChange = (tabId: string) => {
        if (tabId === activeTab || isTransitioning) return;

        setIsTransitioning(true);
        setTimeout(() => {
            setActiveTab(tabId);
            setIsTransitioning(false);
        }, 200);
    };

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="md"
            className="relative overflow-hidden bg-white dark:bg-black"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
            </div>

            <div className="relative z-10">
                {/* Header row */}
                <div className="mb-10">
                    <div>
                        <SectionHeading
                            pill={content.pillLabel}
                            title={content.title}
                            subtitle={content.subtitle}
                            isVisible={sectionRef.isVisible}
                            align="left"
                            maxWidthClass="max-w-5xl"
                            className="!mb-8"
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className="grid lg:grid-cols-[1fr_1fr] gap-8 lg:gap-12 items-start">
                        <div className="space-y-4">
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                        </div>
                        <div className="space-y-4">
                            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-3/4" />
                            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                        </div>
                    </div>
                ) : tabs.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400 text-lg">
                            No services available yet.
                        </p>
                    </div>
                ) : activeTabData ? (
                    <div className="grid lg:grid-cols-[1fr_1fr] gap-8 lg:gap-12 items-start">
                        {/* Left Column */}
                        <div
                            className={`transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                                }`}
                        >
                            {/* Tab Buttons - Flexible width, natural sizing */}
                            <div className="flex flex-wrap justify-center sm:justify-start gap-4 mb-6">
                                {tabs.map((tab) => {
                                    const Icon = tab.icon;
                                    const isActive = activeTab === tab.id;

                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => handleTabChange(tab.id)}
                                            className={cn(
                                                'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 text-left',
                                                'inline-flex transform',
                                                isActive
                                                    ? 'bg-primary-600 text-white hover:bg-primary-700 scale-105 shadow-lg shadow-primary-500/30'
                                                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 hover:scale-105'
                                            )}
                                        >
                                            <Icon
                                                className={cn(
                                                    'h-5 w-5 flex-shrink-0',
                                                    isActive
                                                        ? 'text-white'
                                                        : tab.iconColor || 'text-gray-600 dark:text-gray-400'
                                                )}
                                            />
                                            <span className="text-sm font-medium whitespace-nowrap">{tab.title}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* More Features Button */}
                            <Button
                                href={getLocalizedPagePath(content.moreFeaturesLink, locale)}
                                variant="primary"
                                size="md" className="w-full sm:w-auto justify-center"
                            >
                                {content.exploreMoreButton}
                            </Button>
                        </div>

                        {/* Right Column - Dynamic Content */}
                        <div className="relative">
                            <div
                                key={activeTab}
                                className={cn(
                                    'transition-all duration-500 ease-in-out',
                                    isTransitioning
                                        ? 'opacity-0 translate-x-4 scale-95'
                                        : 'opacity-100 translate-x-0 scale-100'
                                )}
                            >
                                <Typography variant="h3" className="mb-4">
                                    {activeTabData.title}
                                </Typography>
                                <Typography variant="p" className="mb-5 transition-all duration-300">
                                    {activeTabData.description}
                                </Typography>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </SectionWrapper>
    );
}

